import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token from localStorage as fallback
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const createSession = (sessionId) => 
  api.post('/auth/session', null, { headers: { 'X-Session-ID': sessionId } });

export const getMe = () => api.get('/auth/me');

export const logout = () => api.post('/auth/logout');

// Doctors
export const getDoctors = (params) => api.get('/doctors', { params });

export const getDoctor = (doctorId) => api.get(`/doctors/${doctorId}`);

export const createDoctor = (data) => api.post('/doctors', data);

export const updateDoctor = (doctorId, data) => api.put(`/doctors/${doctorId}`, data);

// Reviews
export const getReviews = (doctorId) => api.get(`/reviews/${doctorId}`);

export const createReview = (data) => api.post('/reviews', data);

// Bookings
export const getBookingEnabled = () => api.get('/settings/booking-enabled');

export const createBooking = (data) => api.post('/bookings', data);

export const getBookings = () => api.get('/bookings');

// Analytics
export const trackEvent = (eventType, doctorId, metadata) => 
  api.post('/analytics/track', null, { params: { event_type: eventType, doctor_id: doctorId }, data: metadata });

export const getAnalytics = (doctorId) => 
  api.get('/analytics/dashboard', { params: doctorId ? { doctor_id: doctorId } : {} });

// Payments
export const createCheckout = (packageType) => api.post('/payments/checkout', null, { params: { package: packageType } });

export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);

// Admin
export const getSuperAdmins = () => api.get('/admin/super-admins');

export const addSuperAdmin = (email) => api.post('/admin/super-admins', { email });

export const removeSuperAdmin = (email) => api.delete(`/admin/super-admins/${email}`);

export const toggleBooking = (enabled) => api.post('/admin/settings/booking', null, { params: { enabled } });

// Metadata
export const getSpecialties = () => api.get('/metadata/specialties');

export const getCounties = () => api.get('/metadata/counties');

export const getRegions = () => api.get('/metadata/regions');

export const getInsurances = () => api.get('/metadata/insurances');

export const getDoctorTitles = () => api.get('/metadata/doctor-titles');

// KMPDC
export const verifyKMPDC = (licenseNumber, doctorName) => 
  api.post('/doctors/verify-kmpdc', null, { params: { license_number: licenseNumber, doctor_name: doctorName } });

export const deleteDoctor = (doctorId) => api.delete(`/doctors/${doctorId}`);

// CMS
export const getAllDropdowns = () => api.get('/cms/dropdowns');

export const getDropdownOptions = (fieldName) => api.get(`/cms/dropdowns/${fieldName}`);

export const updateDropdownOptions = (fieldName, options) => 
  api.put(`/cms/dropdowns/${fieldName}`, { field_name: fieldName, options });

export const addDropdownOption = (fieldName, option) => 
  api.post(`/cms/dropdowns/${fieldName}/add`, null, { params: { option } });

export const removeDropdownOption = (fieldName, option) => 
  api.delete(`/cms/dropdowns/${fieldName}/remove`, { params: { option } });

export const getAllTextContent = (page = null) => 
  api.get('/cms/text-content', { params: page ? { page } : {} });

export const getTextContent = (contentKey) => api.get(`/cms/text-content/${contentKey}`);

export const updateTextContent = (contentKey, data) => 
  api.put(`/cms/text-content/${contentKey}`, data);

export const createTextContent = (data) => api.post('/cms/text-content', data);

export const deleteTextContent = (contentKey) => api.delete(`/cms/text-content/${contentKey}`);

// Voice
export const transcribeVoice = (formData, language) =>
  api.post(`/ai/voice/transcribe?language=${language}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Recommendations
export const getRecommendations = () => api.get('/recommendations');

// RBAC / Team Management
export const getTeamMembers = () => api.get('/admin/team-members');
export const addAdminMember = (email, role, permissions) =>
  api.post('/admin/add-member', { email, role, permissions });
export const updatePermissions = (userId, role, permissions) =>
  api.put(`/admin/update-permissions/${userId}`, { role, permissions });
export const revokeAdmin = (userId) => api.post(`/admin/revoke/${userId}`);
export const getAvailablePermissions = () => api.get('/admin/available-permissions');

// Auth extras
export const setPassword = (password, confirmPassword) =>
  api.post('/auth/set-password', { password, confirm_password: confirmPassword });
export const acceptTerms = () => api.post('/auth/accept-terms');

// Content Generation
export const generateContent = (data) => api.post('/ai/generate-content', data);

export default api;